// src-dev/components/video/VideoGridLayout.tsx
import React, { useState, useEffect, useRef } from 'react';
import { ParticipantTile } from './ParticipantTile';
import { Participant } from '../../../interfaces/video/video';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

interface VideoGridLayoutProps {
  participants: Participant[];
}

const getResponsivePageSize = () => {
  const width = window.innerWidth;
  // Use smaller page sizes on narrow devices so tiles can grow and use available space.
  if (width < 360) { // very small phones
    return 1;
  }
  if (width < 768) { // phones / large phones - allow 2 so two participants can sit side-by-side
    return 2;
  }
  if (width < 1200) { // tablets
    return 6;
  }
  return 12; // Desktops
};

// Helper function to get responsive size hint for ParticipantTile only (no layout logic changed)
const getTileSize = (count: number): "xs" | "sm" | "md" | "lg" | "xl" => {
  if (count === 1) return 'lg';
  if (count === 2) return 'md';
  if (count >= 3 && count <= 4) return 'md';
  if (count >= 5 && count <= 9) return 'sm';
  return 'xs';
};

export const VideoGridLayout: React.FC<VideoGridLayoutProps> = ({ participants }) => {
  const [pageSize, setPageSize] = useState(getResponsivePageSize());
  const [currentPage, setCurrentPage] = useState(0);
  const [availableHeight, setAvailableHeight] = useState(() => {
    // reserve ~120px for controls/header/footer area — tweak if your layout has bigger chrome
    return (typeof window !== 'undefined' ? window.innerHeight : 900) - 120;
  });
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [availableWidth, setAvailableWidth] = useState(() => {
    return typeof window !== 'undefined' ? window.innerWidth : 1200;
  });

  useEffect(() => {
    const setSizes = () => {
      setPageSize(getResponsivePageSize());
      // Prefer visualViewport when available (better for mobile browsers with overlays)
      const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      // Reserve less chrome on small screens
      const reserved = window.innerWidth < 768 ? 100 : 120;
      setAvailableHeight(vh - reserved);
      setAvailableWidth(gridRef.current ? gridRef.current.clientWidth : window.innerWidth);
    };

    // Run once on mount to measure the container
    setSizes();

    window.addEventListener('resize', setSizes);
    // orientationchange helps when rotating mobile devices
    window.addEventListener('orientationchange', setSizes);
    // visualViewport may fire on mobile when the on-screen keyboard appears; listen if available
    if ((window as any).visualViewport) {
      (window as any).visualViewport.addEventListener('resize', setSizes);
    }

    return () => {
      window.removeEventListener('resize', setSizes);
      window.removeEventListener('orientationchange', setSizes);
      if ((window as any).visualViewport) {
        (window as any).visualViewport.removeEventListener('resize', setSizes);
      }
    };
  }, []);

  const totalParticipants = participants.length;

  // Decide whether to use a vertical single-column stack (mobile) or a multi-column grid.
  const isVerticalStack = availableWidth < 768;

  // For vertical mode, compute how many rows (tiles) fit per page based on availableHeight.
  // This yields the effective pageSize used for pagination. On wider screens we keep the
  // existing pageSize state which is driven by getResponsivePageSize().
  const minTileHeightForStack = availableWidth < 420 ? 180 : 200; // tune as needed
  const rowsPerPage = isVerticalStack
    ? Math.max(1, Math.floor(availableHeight / Math.max(120, minTileHeightForStack)))
    : undefined;

  // We'll compute the best grid first (cols x rows), then derive effectivePageSize and pagination.


  // Determine tile size hint based on visible count (computed after pagination)

  // Compute optimal grid (cols x rows) for available container so tiles are as large and square as possible.
  const gapPx = 16; // matches gap: '1rem'

  const computeBestGrid = (count: number) => {
    if (isVerticalStack) {
      return { cols: 1, rows: rowsPerPage!, tileSize: Math.max(120, Math.floor((availableHeight - (rowsPerPage! - 1) * gapPx) / rowsPerPage!)) };
    }
    // Desktop: choose columns (1..4) and compute rowsNeeded = ceil(count / cols).
    // For each candidate compute tileWidth and tileHeight available; choose cols that maximize tileSize
    // (min(tileWidth, tileHeight)) so tiles are as square as possible while showing all participants.
    // For small counts (3-4) prefer 2 columns so we get a 2x2 layout for 4 users instead of 4-in-a-row.
    let maxCols = Math.min(4, Math.max(1, count));
    if (count <= 4) {
      maxCols = Math.min(maxCols, 2);
    }
    let best = { cols: 1, rows: Math.max(1, Math.ceil(count / 1)), tileSize: 0 };

    for (let cols = 1; cols <= maxCols; cols++) {
      const totalGapsWidth = Math.max(0, cols - 1) * gapPx;
      const tileWidth = availableWidth > 0 ? Math.floor((availableWidth - totalGapsWidth) / cols) : 200;
      if (tileWidth <= 0) continue;
      const rowsNeeded = Math.max(1, Math.ceil(count / cols));
      const totalGapsHeight = Math.max(0, rowsNeeded - 1) * gapPx;
      const tileHeight = Math.max(120, Math.floor((availableHeight - totalGapsHeight) / rowsNeeded));
      const tileSize = Math.min(tileWidth, tileHeight);

      // Prefer larger tileSize (more square). If tie, prefer more columns.
      if (tileSize > best.tileSize || (tileSize === best.tileSize && cols > best.cols)) {
        best = { cols, rows: rowsNeeded, tileSize };
      }
    }

    // For exactly 4 participants prefer a 2x2 grid when possible
    if (count === 4 && availableWidth > 0) {
      const cols = 2;
      const totalGapsWidth = Math.max(0, cols - 1) * gapPx;
      const tileWidth = Math.floor((availableWidth - totalGapsWidth) / cols);
      const rowsNeeded = 2;
      const totalGapsHeight = Math.max(0, rowsNeeded - 1) * gapPx;
      const tileHeight = Math.max(120, Math.floor((availableHeight - totalGapsHeight) / rowsNeeded));
      const tileSize = Math.min(tileWidth, tileHeight);
      if (tileSize > best.tileSize) {
        best = { cols, rows: rowsNeeded, tileSize };
      }
    }

    return { cols: best.cols, rows: best.rows, tileSize: best.tileSize };
  };
  // Compute grid based on total participants (we choose a grid that fits the container,
  // then paginate participants into pages of capacity = cols * rows)
  const { cols: chosenCols, rows: chosenRows, tileSize: gridTileSize } = computeBestGrid(Math.max(1, totalParticipants));
  const gridCols = chosenCols;
  const rowsUsed = isVerticalStack ? rowsPerPage! : chosenRows;
  // On desktop/web show ALL participants (no pagination). On mobile use rowsPerPage.
  const effectivePageSize = isVerticalStack ? rowsPerPage! : totalParticipants;
  const totalPages = isVerticalStack ? Math.max(1, Math.ceil(totalParticipants / effectivePageSize)) : 1;

  useEffect(() => {
    if (currentPage >= totalPages) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, currentPage]);

  // Paginate using the effective page size (rowsPerPage on mobile). On desktop we show all.
  const paginatedParticipants = isVerticalStack
    ? participants.slice(currentPage * effectivePageSize, (currentPage + 1) * effectivePageSize)
    : participants.slice(0, totalParticipants);
  const isLastPage = isVerticalStack ? currentPage === totalPages - 1 : true;

  const finalRowHeight = gridTileSize;

  // Determine tile size hint based on how many tiles are visible on this page
  const tileSize = getTileSize(4);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
  };

  return (
  <div className="flex w-full flex-col" style={{ minWidth: 0, height: '100%', position: 'relative' }}>
      <div className="flex justify-center w-full h-full px-4 pb-6 pt-2" style={{ minHeight: 0 }}>
        {/* Grid: use CSS Grid so we can control columns precisely (2x2 for 4 users, etc.) */}
        <div
          ref={gridRef}
          className="w-100"
            style={{
            minHeight: 0,
            height: `${availableHeight}px`,
            display: 'grid',
            gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
            gridAutoRows: `${finalRowHeight}px`, // deterministic row height (prefer square when possible)
            gap: '1rem',
            alignItems: 'stretch',
          }}
        >
          {paginatedParticipants.map((participant) => (
            <div style={{ width: '100%', display: 'flex', height: '100%' }}>
              {/* Make each tile stretch to fill grid cell */}
              <div className="w-100 d-flex align-items-stretch" style={{ height: '100%' }}>
                <ParticipantTile participant={participant} size={tileSize} />
              </div>
            </div>
          ))} 
        </div>
      </div>

      {/* Pagination controls - responsive and touch friendly.
          On narrow screens render as an overlay to avoid being hidden by bottom chrome. */}
  {isVerticalStack && totalPages > 1 && (
        <div
          className={availableWidth < 576 ? '' : 'd-flex flex-column flex-sm-row align-items-center justify-content-center p-2 text-white mt-3 gap-2'}
          style={availableWidth < 576 ? {
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 60,
            backgroundColor: 'rgba(0,0,0,0.45)',
            padding: '6px 8px',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          } : undefined}
        >
          <div className="d-flex align-items-center gap-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              className="btn btn-dark btn-sm d-flex align-items-center justify-content-center"
              aria-label="Previous page"
              style={{ minWidth: 44, minHeight: 44 }}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className={availableWidth < 576 ? 'small text-white' : 'mx-2 d-none d-sm-inline'}>Page {currentPage + 1} of {totalPages}</span>
            <span className={availableWidth < 576 ? 'd-none' : 'mx-2 d-sm-none small'}>Pg {currentPage + 1}/{totalPages}</span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages - 1}
              className="btn btn-dark btn-sm d-flex align-items-center justify-content-center"
              aria-label="Next page"
              style={{ minWidth: 44, minHeight: 44 }}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};